import {
  computed,
  readonly,
  ref,
  toValue,
  watch,
  type MaybeRefOrGetter,
  type Ref
} from 'vue'
import { useDebounce } from '@vueuse/core'

import {
  DEFAULT_PAGINATION_LIMIT,
  SEARCH_DEBOUNCE_MS
} from '@/shared/constants'

import { provideListErrorOf } from './listErrorState'
import type { EntityListParams } from './types'
import { showMutationError } from './mutationError'
import { useConfirm } from './useConfirm'

/** The shape `createEntityQueries().useList` returns. */
type ListQuery<TItem, TParams extends EntityListParams = EntityListParams> = (
  params: MaybeRefOrGetter<TParams>,
  options?: { enabled?: MaybeRefOrGetter<boolean> }
) => {
  items: Ref<TItem[]>
  total: Ref<number>
  isLoading: Ref<boolean>
  isFetching: Ref<boolean>
  isPlaceholderData: Ref<boolean>
  isFetched: Ref<boolean>
  isError: Ref<boolean>
  error: Ref<unknown>
  refetch: () => unknown
}

const hasEntries = (value: Record<string, unknown> | undefined) =>
  !!value && Object.keys(value).length > 0

/**
 * The confirm sentence when `remove` does not bring its own. Read defensively rather
 * than constraining the generic: most records call themselves `name`, and the ones
 * that do not pass their own message anyway.
 */
const defaultRemoveMessage = (item: unknown) => {
  const name = (item as { name?: unknown })?.name

  return typeof name === 'string' && name.trim()
    ? `Are you sure to delete ${name}?`
    : 'Are you sure to delete this record?'
}

/**
 * A paginated list backed by an entity's list query.
 *
 * A table widget's script is this call: it owns the page, the request, the row-level
 * pending state and the delete, and the SFC binds what it returns.
 *
 * **Pagination is local to the caller**, one page per mounted table, because two tables of
 * the same entity can be on screen at once and a pinia store is a singleton. The search
 * term does come from the store — the page toolbar writes it and the table reads it.
 *
 * Narrowing the list — a new search or a new filter — sends the user back to page one,
 * so an out-of-range page can never be requested. A list that got *shorter* under a page
 * the user is already on is clamped back to the last one that exists.
 */
export function useEntityTable<
  TItem,
  // Inferred from the query hook the caller hands in: an entity that declared its filter
  // names (`EntityListParamsFor`) narrows `filter` below to those names, and one that has
  // not keeps the open `ListFilter`.
  TParams extends EntityListParams = EntityListParams,
  // The filter is almost always written as `filter: () => ({ … })`, and TypeScript does *not*
  // check an object literal returned from an arrow for keys the target does not have — a
  // misspelled key passes whenever a correctly spelled one sits beside it. This constraint says
  // it a second way: every key the entity's filter does not declare has to be `never`, which
  // nothing is. An entity that declared no filter constrains nothing.
  TFilter extends NonNullable<TParams['filter']> &
    Record<
      Exclude<keyof TFilter, keyof NonNullable<TParams['filter']>>,
      never
    > = NonNullable<TParams['filter']>
>(config: {
  query: ListQuery<TItem, TParams>

  search?: MaybeRefOrGetter<string | null | undefined>
  /**
   * Whether the search is held back while it is being typed. On by default, which is
   * what a toolbar search box wants. Pass `false` when the term arrives already
   * settled — from a filter panel's Apply, say — where waiting only buys one extra
   * request for the un-searched list and a flash of rows the user did not ask for.
   */
  debounceSearch?: boolean
  filter?: MaybeRefOrGetter<TFilter | undefined>
  sort?: Record<string, 'asc' | 'desc'>

  /**
   * Whether the list may be requested at all. Defaults to "every filter value is
   * set" — a filter of `{ platform_id: undefined }` would otherwise ask the backend
   * for everyone's rows while the id is still travelling in from the route.
   */
  enabled?: MaybeRefOrGetter<boolean>

  limit?: number

  remove?: {
    mutate: (
      id: string | number,
      options?: { onError?: (error: unknown) => void }
    ) => void
    /**
     * The confirm text. Leave it out and a record that calls itself `name` gets "Are you
     * sure to delete <name>?". Pass one when the record names itself something else
     * (`domain`, `spotName`) or the action is not a plain delete ("archive", "delete the
     * blocker for …").
     */
    message?: (item: TItem) => string
  }
}) {
  const page = ref(1)
  const limit = ref(config.limit ?? DEFAULT_PAGINATION_LIMIT)

  const typedSearch = computed(() => toValue(config.search) || undefined)
  const debouncedSearch = useDebounce(typedSearch, SEARCH_DEBOUNCE_MS)

  const search = computed(() =>
    config.debounceSearch === false ? typedSearch.value : debouncedSearch.value
  )

  const filter = computed(() => toValue(config.filter))

  // Written key by key and checked with `satisfies` on the literal itself. Neither a
  // literal containing a spread nor one annotated only through the `computed<…>` around it
  // is checked for keys it should not have, so a misspelled `limitt` passes both — do not
  // rewrite this into either shape. `undefined` is the same request as an absent key — the
  // serializer skips both, and so does the query key's hash — and it is a key the compiler
  // can see.
  //
  // What this still cannot check is a name *inside* `filter`: those are the backend's,
  // one list at a time (see `ListFilter`).
  const listParams = computed(
    () =>
      ({
        limit: limit.value,
        page: page.value,
        query: search.value || undefined,
        filter: hasEntries(filter.value) ? filter.value : undefined,
        sort: config.sort
      }) as TParams
  )

  const enabled = computed(() => {
    if (config.enabled !== undefined) return toValue(config.enabled)
    if (!filter.value) return true

    return Object.values(filter.value).every(
      (value) => value !== undefined && value !== null && value !== ''
    )
  })

  // `sync` on purpose: the query key is recomputed as soon as the filter changes, so a
  // deferred reset would fire one request for the new filter on the old page and a
  // second one after the reset.
  //
  // The filter is compared by value, for the reason `useEntitySelect` gives at its own
  // paging reset: `route.query` is a new object after every navigation even when the query
  // string is byte-identical, and the `useFilters` → `useFilterPanel` → `listFilter` chain
  // rebuilds the object on every render — so watching the object itself would send a
  // filtered list back to page one whenever a modal route opens over it.
  watch(
    [search, () => JSON.stringify(filter.value ?? null)],
    () => {
      page.value = 1
    },
    { flush: 'sync' }
  )

  const query = config.query(listParams, { enabled })

  /**
   * A list that got shorter can leave `page` past its end — a delete on the last page, or a
   * narrower result. `Pagination` hides itself once one page is enough (`total <= limit`),
   * so an unclamped page is not a wrong number but a dead end: an empty grid, no paginator,
   * and no empty state either, because `useTableEmptySlot` correctly refuses to claim the
   * account has no records while `total` is non-zero.
   *
   * `isFetching` is watched, not just read: under `keepPreviousData` `total` is still the
   * previous page's while a request is in flight, and a clamp skipped for that reason has
   * to be retried when the list settles rather than waiting for `total` to change again.
   */
  watch([query.total, limit, query.isFetching], ([total, size, fetching]) => {
    if (fetching) return

    const lastPage = Math.max(1, Math.ceil(total / size))
    if (page.value > lastPage) page.value = lastPage
  })

  /**
   * A refused list is not an empty one. Without this the table renders the page's "you
   * have no records yet" panel on a 401, a 403 or a dropped connection, which tells the
   * publisher their data is gone. `Table` reads it from here, so no widget has to.
   */
  provideListErrorOf(query)

  function onPagination(payload: { newPage: number; limit: number }) {
    page.value = payload.newPage
    limit.value = payload.limit
  }

  const { confirm } = useConfirm()

  async function onDelete(item: TItem & { id: string | number }) {
    if (!config.remove) return

    const ok = await confirm(
      config.remove.message?.(item) ?? defaultRemoveMessage(item)
    )

    if (!ok) return

    // A refused delete has to say why — otherwise the row simply stays and the reason the
    // API gave ("Not authorized…") is thrown away. The retry re-runs the confirmed delete
    // rather than asking again: the user has already answered that question, and a timeout
    // is the failure the offer is for.
    const remove = () =>
      config.remove?.mutate(item.id, {
        onError: (error) =>
          showMutationError(error, 'Could not delete the record.', {
            retry: remove
          })
      })

    remove()
  }

  return {
    items: query.items,
    total: query.total,
    // `isPlaceholderData`, not `isFetching`: under `keepPreviousData` it is true exactly
    // while the previous key's rows stand in for a new page, filter or search — the case
    // the grid mask is for — and false for a background refetch of the key already on
    // screen. `isFetching` is a superset, and greys the whole table out on every
    // window-focus revalidation and after every single-row mutation, drowning the row-level
    // pending state.
    isLoading: computed(
      () => query.isLoading.value || query.isPlaceholderData.value
    ),
    isFetched: query.isFetched,
    isError: query.isError,
    refetch: query.refetch,

    pageModel: page,
    // Read-only: a page size is changed through `onPagination`, which sets the page
    // beside it.
    limitForPagination: readonly(limit),
    onPagination,

    onDelete
  }
}
